import { useState, useMemo } from "react";
import {
  X,
  Check,
  AlertTriangle,
  ChevronDown,
  Search,
  MapPin,
  Building2,
  Layers,
} from "lucide-react";
import { findBestMatch, findAllMatches, isExactMatch } from "../lib/fuzzyMatch";
import { type ParsedProjectData } from "../lib/csvImport";

// Utility to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

interface ProjectConfirmationModalProps {
  isOpen: boolean;
  importedProjectName: string;
  availableProjects: { id: string; name: string }[];
  existingProjectNames: string[];
  onConfirm: (projectName: string) => void;
  onCancel: () => void;
  projectData?: ParsedProjectData; // Full project data for preview
}

type ModalState = "suggestion" | "dropdown" | "exists" | "existsSuggestion";

export function ProjectConfirmationModal({
  isOpen,
  importedProjectName,
  availableProjects,
  existingProjectNames,
  onConfirm,
  onCancel,
  projectData,
}: ProjectConfirmationModalProps) {
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Determine modal state and best match
  const { modalState, bestMatch, allMatches, existingMatch } = useMemo(() => {
    // Check if project already exists in Claims App (exact match)
    const exactExists = existingProjectNames.some((name) =>
      isExactMatch(importedProjectName, name),
    );

    if (exactExists) {
      return {
        modalState: "exists" as ModalState,
        bestMatch: null,
        allMatches: [],
        existingMatch: null,
      };
    }

    // Check for FUZZY match against existing projects first
    const existingFuzzy = findBestMatch(
      importedProjectName,
      existingProjectNames,
      0.6,
    );
    if (existingFuzzy && existingFuzzy.score >= 0.7) {
      return {
        modalState: "existsSuggestion" as ModalState,
        bestMatch: null,
        allMatches: [],
        existingMatch: existingFuzzy,
      };
    }

    // Find best match from available Dashpivot projects
    const availableNames = availableProjects.map((p) => p.name);
    const best = findBestMatch(importedProjectName, availableNames, 0.6);
    const all = findAllMatches(importedProjectName, availableNames, 0.3);

    if (best && best.score >= 0.7) {
      return {
        modalState: "suggestion" as ModalState,
        bestMatch: best,
        allMatches: all,
        existingMatch: null,
      };
    }

    return {
      modalState: "dropdown" as ModalState,
      bestMatch: best,
      allMatches: all,
      existingMatch: null,
    };
  }, [importedProjectName, availableProjects, existingProjectNames]);

  // Filter projects for dropdown
  const filteredProjects = useMemo(() => {
    if (!searchTerm) return availableProjects;
    return availableProjects.filter((p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [availableProjects, searchTerm]);

  if (!isOpen) return null;

  const handleConfirmMatch = () => {
    if (bestMatch) {
      onConfirm(bestMatch.match);
    }
  };

  const handleSelectProject = (name: string) => {
    setSelectedProject(name);
    setShowDropdown(false);
  };

  const handleConfirmSelected = () => {
    if (selectedProject) {
      onConfirm(selectedProject);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 650 }}
      >
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title">
            {modalState === "exists"
              ? "Project Already Exists"
              : "Confirm Project Name"}
          </h3>
          <button className="btn btn-ghost" onClick={onCancel}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Project Already Exists */}
          {modalState === "exists" && (
            <div
              style={{
                textAlign: "center",
                padding: "var(--spacing-lg)",
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "rgba(239, 68, 68, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto var(--spacing-lg)",
                }}
              >
                <AlertTriangle size={32} color="var(--color-error)" />
              </div>
              <h4 style={{ marginBottom: 8 }}>Project Already Exists</h4>
              <p style={{ color: "var(--text-secondary)", marginBottom: 16 }}>
                The project "<strong>{importedProjectName}</strong>" is already
                in Claims App.
              </p>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text-muted)",
                  background: "var(--bg-tertiary)",
                  padding: "var(--spacing-md)",
                  borderRadius: "var(--border-radius-md)",
                }}
              >
                This project is already in Claims App. You can manage it from
                the Projects page.
              </p>
            </div>
          )}

          {/* Similar Project Already Exists (Fuzzy Match) */}
          {modalState === "existsSuggestion" && existingMatch && (
            <div
              style={{
                textAlign: "center",
                padding: "var(--spacing-lg)",
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "rgba(245, 158, 11, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto var(--spacing-lg)",
                }}
              >
                <AlertTriangle size={32} color="var(--color-warning)" />
              </div>
              <h4 style={{ marginBottom: 8 }}>Similar Project Found</h4>
              <p style={{ color: "var(--text-secondary)", marginBottom: 8 }}>
                You're trying to import:
              </p>
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  padding: "var(--spacing-md)",
                  borderRadius: "var(--border-radius-md)",
                  marginBottom: "var(--spacing-md)",
                  fontWeight: 500,
                }}
              >
                "{importedProjectName}"
              </div>
              <p style={{ color: "var(--text-secondary)", marginBottom: 8 }}>
                But a similar project already exists:
              </p>
              <div
                style={{
                  background: "rgba(245, 158, 11, 0.1)",
                  border: "2px solid var(--color-warning)",
                  padding: "var(--spacing-md)",
                  borderRadius: "var(--border-radius-md)",
                  marginBottom: "var(--spacing-md)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                }}
              >
                <span style={{ fontWeight: 600 }}>{existingMatch.match}</span>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    background: "var(--bg-secondary)",
                    padding: "2px 8px",
                    borderRadius: 12,
                  }}
                >
                  {Math.round(existingMatch.score * 100)}% match
                </span>
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text-muted)",
                  background: "var(--bg-tertiary)",
                  padding: "var(--spacing-md)",
                  borderRadius: "var(--border-radius-md)",
                }}
              >
                If this is the same project, cancel and manage it from the
                Projects page instead.
              </p>
            </div>
          )}

          {/* Good Match Suggestion */}
          {modalState === "suggestion" && bestMatch && (
            <div>
              <p style={{ marginBottom: "var(--spacing-md)" }}>
                The imported project name:
              </p>
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  padding: "var(--spacing-md)",
                  borderRadius: "var(--border-radius-md)",
                  marginBottom: "var(--spacing-lg)",
                  fontWeight: 500,
                }}
              >
                "{importedProjectName}"
              </div>

              <p style={{ marginBottom: "var(--spacing-md)" }}>
                Did you mean this Dashpivot project?
              </p>
              <div
                style={{
                  background: "rgba(16, 185, 129, 0.1)",
                  border: "2px solid var(--color-success)",
                  padding: "var(--spacing-md)",
                  borderRadius: "var(--border-radius-md)",
                  marginBottom: "var(--spacing-lg)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Check size={20} color="var(--color-success)" />
                <span style={{ fontWeight: 600 }}>{bestMatch.match}</span>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 12,
                    color: "var(--text-muted)",
                    background: "var(--bg-secondary)",
                    padding: "2px 8px",
                    borderRadius: 12,
                  }}
                >
                  {Math.round(bestMatch.score * 100)}% match
                </span>
              </div>

              {allMatches.length > 1 && (
                <button
                  className="btn btn-ghost"
                  onClick={() => setShowDropdown(!showDropdown)}
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    fontSize: 13,
                  }}
                >
                  Choose a different project
                  <ChevronDown
                    size={16}
                    style={{
                      transform: showDropdown ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </button>
              )}
            </div>
          )}

          {/* Dropdown Selection */}
          {(modalState === "dropdown" || showDropdown) && (
            <div
              style={{
                marginTop:
                  modalState === "suggestion" ? "var(--spacing-md)" : 0,
              }}
            >
              {modalState === "dropdown" && (
                <>
                  <p style={{ marginBottom: "var(--spacing-md)" }}>
                    The imported project name:
                  </p>
                  <div
                    style={{
                      background: "var(--bg-tertiary)",
                      padding: "var(--spacing-md)",
                      borderRadius: "var(--border-radius-md)",
                      marginBottom: "var(--spacing-lg)",
                      fontWeight: 500,
                    }}
                  >
                    "{importedProjectName}"
                  </div>
                  <p style={{ marginBottom: "var(--spacing-md)" }}>
                    <AlertTriangle
                      size={16}
                      color="var(--color-warning)"
                      style={{ display: "inline", marginRight: 6 }}
                    />
                    No close match found. Please select the correct project:
                  </p>
                </>
              )}

              {/* Search */}
              <div
                style={{
                  position: "relative",
                  marginBottom: "var(--spacing-md)",
                }}
              >
                <Search
                  size={16}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--text-muted)",
                  }}
                />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: 36 }}
                />
              </div>

              {/* Project List */}
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid var(--border-color)",
                  borderRadius: "var(--border-radius-md)",
                }}
              >
                {filteredProjects.length === 0 ? (
                  <p
                    style={{
                      padding: "var(--spacing-md)",
                      color: "var(--text-muted)",
                      textAlign: "center",
                    }}
                  >
                    No projects found
                  </p>
                ) : (
                  filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      type="button"
                      onClick={() => handleSelectProject(project.name)}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "var(--spacing-sm) var(--spacing-md)",
                        textAlign: "left",
                        background:
                          selectedProject === project.name
                            ? "var(--bg-tertiary)"
                            : "transparent",
                        border: "none",
                        borderBottom: "1px solid var(--border-color-light)",
                        cursor: "pointer",
                        fontSize: 14,
                        color: "var(--text-primary)",
                      }}
                    >
                      {selectedProject === project.name && (
                        <Check
                          size={14}
                          color="var(--color-success)"
                          style={{ display: "inline", marginRight: 8 }}
                        />
                      )}
                      {project.name}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Project Details Preview - shows all imported data */}
          {projectData &&
            modalState !== "exists" &&
            modalState !== "existsSuggestion" && (
              <div
                style={{
                  marginTop: "var(--spacing-lg)",
                  borderTop: "1px solid var(--border-color)",
                  paddingTop: "var(--spacing-lg)",
                }}
              >
                <h4
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    marginBottom: "var(--spacing-md)",
                    color: "var(--text-secondary)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Layers size={16} />
                  Project Details to Import
                </h4>

                {/* Project Info */}
                <div
                  style={{
                    background: "var(--bg-secondary)",
                    borderRadius: "var(--border-radius-md)",
                    padding: "var(--spacing-md)",
                    marginBottom: "var(--spacing-md)",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "var(--spacing-sm)",
                      fontSize: 13,
                    }}
                  >
                    {projectData.job_address && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8,
                        }}
                      >
                        <MapPin
                          size={14}
                          style={{
                            color: "var(--text-muted)",
                            marginTop: 2,
                            flexShrink: 0,
                          }}
                        />
                        <div>
                          <div
                            style={{ color: "var(--text-muted)", fontSize: 11 }}
                          >
                            Address
                          </div>
                          <div style={{ fontWeight: 500 }}>
                            {projectData.job_address}
                          </div>
                        </div>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                      }}
                    >
                      <Check
                        size={14}
                        style={{
                          color:
                            projectData.status === "active"
                              ? "var(--color-success)"
                              : "var(--text-muted)",
                          marginTop: 2,
                          flexShrink: 0,
                        }}
                      />
                      <div>
                        <div
                          style={{ color: "var(--text-muted)", fontSize: 11 }}
                        >
                          Status
                        </div>
                        <div
                          style={{
                            fontWeight: 500,
                            textTransform: "capitalize",
                          }}
                        >
                          {projectData.status}
                        </div>
                      </div>
                    </div>
                  </div>
                  {projectData.project_details && (
                    <div
                      style={{
                        marginTop: "var(--spacing-sm)",
                        paddingTop: "var(--spacing-sm)",
                        borderTop: "1px solid var(--border-color-light)",
                        fontSize: 13,
                        color: "var(--text-secondary)",
                      }}
                    >
                      {projectData.project_details}
                    </div>
                  )}
                </div>

                {/* Builder Info */}
                {(projectData.builder_name || projectData.builder_email) && (
                  <div
                    style={{
                      background: "rgba(59, 130, 246, 0.05)",
                      borderRadius: "var(--border-radius-md)",
                      padding: "var(--spacing-md)",
                      marginBottom: "var(--spacing-md)",
                      border: "1px solid rgba(59, 130, 246, 0.1)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <Building2
                        size={14}
                        style={{ color: "var(--color-primary)" }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--color-primary)",
                        }}
                      >
                        Builder
                      </span>
                    </div>
                    <div style={{ fontSize: 13 }}>
                      {projectData.builder_name && (
                        <div style={{ fontWeight: 500 }}>
                          {projectData.builder_name}
                        </div>
                      )}
                      {projectData.builder_poc_name && (
                        <div
                          style={{
                            color: "var(--text-secondary)",
                            fontSize: 12,
                          }}
                        >
                          Contact: {projectData.builder_poc_name}
                          {projectData.builder_email && (
                            <>
                              {" "}
                              •{" "}
                              <a
                                href={`mailto:${projectData.builder_email}`}
                                style={{ color: "var(--color-primary)" }}
                              >
                                {projectData.builder_email}
                              </a>
                            </>
                          )}
                        </div>
                      )}
                      {projectData.builder_address && (
                        <div
                          style={{
                            color: "var(--text-muted)",
                            fontSize: 12,
                            marginTop: 4,
                          }}
                        >
                          {projectData.builder_address}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Rates Row */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "var(--spacing-sm)",
                    marginBottom: "var(--spacing-md)",
                  }}
                >
                  <div
                    style={{
                      background: "var(--bg-secondary)",
                      borderRadius: "var(--border-radius-sm)",
                      padding: "var(--spacing-sm)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      Normal Rate
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      ${projectData.normal_rate}/hr
                    </div>
                  </div>
                  <div
                    style={{
                      background: "var(--bg-secondary)",
                      borderRadius: "var(--border-radius-sm)",
                      padding: "var(--spacing-sm)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      Overtime Rate
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      ${projectData.overtime_rate}/hr
                    </div>
                  </div>
                  <div
                    style={{
                      background: "var(--bg-secondary)",
                      borderRadius: "var(--border-radius-sm)",
                      padding: "var(--spacing-sm)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      Sat. Non-Contract Rate
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      ${projectData.saturday_rate}/hr
                    </div>
                  </div>
                  <div
                    style={{
                      background: "var(--bg-secondary)",
                      borderRadius: "var(--border-radius-sm)",
                      padding: "var(--spacing-sm)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      Sat. Contract OT Rate
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      ${projectData.saturday_contract_ot_rate || projectData.overtime_rate}/hr
                    </div>
                  </div>
                </div>

                {/* Categories Table */}
                <div
                  style={{
                    border: "1px solid var(--border-color)",
                    borderRadius: "var(--border-radius-md)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      background: "var(--bg-tertiary)",
                      padding: "var(--spacing-sm) var(--spacing-md)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: "1px solid var(--border-color)",
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600 }}>
                      Categories ({projectData.categories.length})
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--color-primary)",
                      }}
                    >
                      Total:{" "}
                      {formatCurrency(
                        projectData.categories.reduce((sum, cat) => {
                          if (cat.isFixedAmount) return sum + cat.fixedAmount;
                          return (
                            sum +
                            cat.subcategories.reduce(
                              (s, sub) => s + sub.amount,
                              0,
                            )
                          );
                        }, 0),
                      )}
                    </span>
                  </div>
                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    {projectData.categories.map((cat, idx) => (
                      <div
                        key={idx}
                        style={{
                          borderBottom:
                            idx < projectData.categories.length - 1
                              ? "1px solid var(--border-color-light)"
                              : "none",
                        }}
                      >
                        <div
                          style={{
                            padding: "var(--spacing-sm) var(--spacing-md)",
                            background: "var(--bg-secondary)",
                            fontWeight: 500,
                            fontSize: 13,
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>{cat.name}</span>
                          {cat.isFixedAmount && (
                            <span data-seq-private="" style={{ color: "var(--color-primary)" }}>
                              {formatCurrency(cat.fixedAmount)}
                            </span>
                          )}
                        </div>
                        {!cat.isFixedAmount &&
                          cat.subcategories.map((sub, subIdx) => (
                            <div
                              key={subIdx}
                              style={{
                                padding: "6px var(--spacing-md) 6px 32px",
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 12,
                                color: "var(--text-secondary)",
                              }}
                            >
                              <span>{sub.name}</span>
                              <span data-seq-private="" style={{ fontWeight: 500 }}>
                                {formatCurrency(sub.amount)}
                              </span>
                            </div>
                          ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel}>
            Cancel Import
          </button>

          {modalState === "exists" && (
            <button className="btn btn-primary" onClick={onCancel}>
              Got It
            </button>
          )}

          {modalState === "existsSuggestion" && (
            <button className="btn btn-primary" onClick={onCancel}>
              Got It
            </button>
          )}

          {modalState === "suggestion" && !showDropdown && (
            <button className="btn btn-primary" onClick={handleConfirmMatch}>
              <Check size={16} />
              Yes, Use This Project
            </button>
          )}

          {(modalState === "dropdown" || showDropdown) && (
            <button
              className="btn btn-primary"
              onClick={handleConfirmSelected}
              disabled={!selectedProject}
            >
              <Check size={16} />
              Confirm Selection
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
